import { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { TOAST_TYPE } from '@base'
import { Button, Input, Select } from '@baseV2'
import { getOrderById, getUserByID, getUsersOfOrganisation, updateReceiverDetails } from '@data'
import { showLoader, showToast } from '@data/state/action'
import { updateDrawerMeta } from '@data/state/action/root'
import { CountryCodes, INSPECTION_USER_DESIGNATIONS, strings } from '@constants'
import { getCountryDialCode, getNumberWithCountryCode, mobileLength } from '@utils'
import BasicModal from '@base/basic/BasicModal'

import { ModalTitle, ModalBody, MobileRow, MobileFieldGroup, CountryCodeWrapper, ModalActions, FieldErrorText } from './style'

const initialFormState = {
	full_name: {
		id: 'full_name',
		label: 'Name',
		placeholder: 'Enter full name',
		value: '',
		dirty: false,
		required: true,
		valid: null,
	},
	mobile_number: {
		id: 'mobile_number',
		label: 'Mobile',
		placeholder: 'Enter mobile number',
		value: '',
		dirty: false,
		required: true,
		valid: null,
	},
	email_id: {
		id: 'email_id',
		label: 'Email',
		placeholder: 'Enter email',
		value: '',
		dirty: false,
		required: false,
	},
	designation: {
		id: 'designation',
		label: 'Designation',
		placeholder: 'Enter designation',
		value: '',
		dirty: false,
		required: false,
	},
}

const deriveFormStateFromData = (oldState = initialFormState, data) => ({
	...oldState,
	full_name: { ...oldState.full_name, value: data?.full_name || '', dirty: false },
	mobile_number: { ...oldState.mobile_number, value: data?.mobile_number?.slice(2) || '', dirty: false, valid: null },
	email_id: { ...oldState.email_id, value: data?.email_id || '', dirty: false },
	designation: { ...oldState.designation, value: data?.designation || '', dirty: false },
})

const defaultCountryCode = CountryCodes.find((_c) => _c.default)
const designationTypes = Object.entries(INSPECTION_USER_DESIGNATIONS).map(([key, value]) => ({ key, label: value }))
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const AddSiteManagerModal = ({ isOpen, onClose, order, invoiceData, organisationId, userId, editable = true }) => {
	const dispatch = useDispatch()
	const user = useSelector((state) => state.getIn(['user', 'user']))

	const [countryCode, setCountryCode] = useState(defaultCountryCode)
	const [formState, setFormState] = useState(initialFormState)
	const [userList, setUserList] = useState([])
	const [selectedUser, setSelectedUser] = useState(null)

	const hasSiteManager = !!userId

	useEffect(() => {
		if (!isOpen) return
		if (!userId) {
			resetHandler()
			return
		}
		getUserByID(userId)
			.then((response) => {
				const code = response?.mobile_number?.slice(0, 2)
				const result = CountryCodes.find((item) => item.dial_code.includes(code)) || getCountryDialCode(code)
				if (result) setCountryCode(result)
				setFormState((_s) => deriveFormStateFromData(_s, response))
			})
			.catch(console.log)
	}, [userId, isOpen])

	useEffect(() => {
		if (!isOpen || !organisationId) return
		dispatch(showLoader(true))
		getUsersOfOrganisation(organisationId)
			.then((response) => setUserList(response || []))
			.catch((error) => {
				const msg = error?.response?.data?.message ?? strings('msg_failed_to_fetch')
				dispatch(showToast(true, msg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => dispatch(showLoader(false)))
	}, [organisationId, isOpen])

	const updateState = (key, updates) => {
		setFormState((_s) => ({ ..._s, [key]: { ..._s[key], ...updates } }))
	}

	const handleUserSelect = (value) => {
		setFormState((_s) => deriveFormStateFromData(_s, value))
		setSelectedUser(value)
	}

	const handleDesignationChange = (value) => {
		updateState('designation', { value: value?.key ?? '' })
	}

	const resetHandler = () => {
		setFormState(initialFormState)
		setSelectedUser(null)
		setCountryCode(defaultCountryCode)
	}

	const submitHandler = () => {
		dispatch(showLoader(true))
		const mobileWithCode = getNumberWithCountryCode(countryCode, formState.mobile_number.value)
		const name = formState.full_name.value
		const email = formState.email_id.value
		const designation = formState.designation.value

	
		const apiCall = updateReceiverDetails( name,mobileWithCode, email,designation, invoiceData?.invoice_number, user?.user_id,order?.order_id)

		apiCall
			.then(() => {
				const toastMsg = hasSiteManager
					? strings(['update', 'site', 'manager', 'successfully'])
					: strings(['add', 'site', 'manager', 'successfully'])
				dispatch(showToast(true, toastMsg, { type: TOAST_TYPE.SUCCESS }))
				return getOrderById(order?.order_id)
			})
			.then((response) => {
				const updatedInvoice = response?.buyer_list?.[0]?.buyer_invoices?.find((i) => i.invoice_number === invoiceData?.invoice_number)
				dispatch(updateDrawerMeta({ order: response, invoiceData: updatedInvoice ?? invoiceData }))
				onClose()
			})
			.catch((error) => {
				const msg = error?.response?.data?.message || error?.data?.message || strings('msg_something_went_wrong')
				dispatch(showToast(true, msg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => dispatch(showLoader(false)))
	}

	const formErrors = useMemo(() => {
		const errors = {}
		if (!formState.full_name.value.trim()) {
			errors.full_name = strings('name_required')
		}
		if (!formState.mobile_number.value) {
			errors.mobile_number = strings(['mobile', 'number', 'is', 'required'])
		} else if (formState.mobile_number.value.length !== mobileLength(countryCode.dial_code)) {
			errors.mobile_number = strings(['mobile', 'number', 'is', 'invalid'])
		}
		if (formState.email_id.value && !EMAIL_REGEX.test(formState.email_id.value)) {
			errors.email_id = strings('enter_valid_email')
		}
		return errors
	}, [formState.full_name.value, formState.mobile_number.value, formState.email_id.value, countryCode.dial_code])

	const disabledHandler = useMemo(() => {
		if (selectedUser) return false
		return Object.keys(formErrors).length > 0
	}, [formErrors, selectedUser])

	const isUserSelectDisabled = useMemo(() => {
		if (!editable) return true
		const { full_name, mobile_number, email_id, designation } = formState
		return !selectedUser && Boolean(full_name.value || mobile_number.value || email_id.value || designation.value)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editable, formState.full_name.value, formState.mobile_number.value, formState.email_id.value, formState.designation.value])

	// View-only users reach this modal through the "(View Details)" link, so every field is
	// locked and the footer actions are hidden.
	const areAllFieldsDisabled = !editable || !!selectedUser

	const title = hasSiteManager ? strings(['edit', 'site', 'manager']) : strings(['add', 'site', 'manager'])

	return (
		<BasicModal isOpen={isOpen} onClose={onClose} width='40vw' closeIcon={true}>
			<ModalTitle>{title}</ModalTitle>
			<ModalBody>
				<Select
					id='select-user'
					label={strings('user')}
					placeholder={strings('select', 'user')}
					data={userList ?? []}
					value={userList?.find((item) => item.user_id === selectedUser?.user_id) ?? {}}
					primaryKey='user_id'
					displayKey='full_name'
					onChange={handleUserSelect}
					disabled={isUserSelectDisabled}
				/>

				<Input
					id={formState.full_name.id}
					label={formState.full_name.label}
					placeholder={formState.full_name.placeholder}
					value={formState.full_name.value ?? ''}
					required={formState.full_name.required}
					error={formState.full_name.dirty ? formErrors.full_name : undefined}
					onChange={(e) => updateState('full_name', { value: e.target.value, dirty: true })}
					disabled={areAllFieldsDisabled}
				/>

				<MobileFieldGroup>
					<MobileRow>
						<CountryCodeWrapper>
							<Select
								id='login-country-code'
								data={CountryCodes}
								displayKey='dial_code'
								primaryKey='name'
								value={countryCode}
								onChange={setCountryCode}
								type='tel'
								noDropIcon
								disabled={areAllFieldsDisabled}
							/>
						</CountryCodeWrapper>
						<Input
							id={formState.mobile_number.id}
							type='tel'
							label={formState.mobile_number.label}
							placeholder={formState.mobile_number.placeholder}
							value={formState.mobile_number.value ?? ''}
							maxLength={mobileLength(countryCode.dial_code)}
							required={formState.mobile_number.required}
							onChange={(e) => updateState('mobile_number', { value: e.target.value, dirty: true })}
							disabled={areAllFieldsDisabled}
						/>
					</MobileRow>
					{formState.mobile_number.dirty && formErrors.mobile_number && <FieldErrorText>{formErrors.mobile_number}</FieldErrorText>}
				</MobileFieldGroup>

				<Input
					id={formState.email_id.id}
					label={formState.email_id.label}
					type='email'
					placeholder={formState.email_id.placeholder}
					value={formState.email_id.value}
					error={formState.email_id.dirty ? formErrors.email_id : undefined}
					onChange={(e) => updateState('email_id', { value: e.target.value, dirty: true })}
					required={formState.email_id.required}
					disabled={areAllFieldsDisabled}
				/>

				<Select
					id='select-designation'
					label={strings('designation')}
					placeholder={strings('select', 'designation')}
					data={designationTypes ?? []}
					value={designationTypes.find((item) => item.key === formState.designation.value) ?? {}}
					disabled={areAllFieldsDisabled}
					primaryKey='key'
					displayKey='label'
					required={formState.designation.required}
					onChange={handleDesignationChange}
				/>
			</ModalBody>

			{editable && (
				<ModalActions>
					<Button small type='tertiary' variant='destructive' onClick={resetHandler}>
						{strings('reset')}
					</Button>
					<Button small disabled={disabledHandler} onClick={submitHandler}>
						{hasSiteManager ? strings('update') : strings('submit')}
					</Button>
				</ModalActions>
			)}
		</BasicModal>
	)
}

export default AddSiteManagerModal
